import { appUrl } from './appBase.js';
import { EMULATOR_LANG_EXTRA } from './emulatorLangExtra.js';
import { EJS_DEFAULT_CONTROLS, SYSTEMS } from './systems.js';
import {
  cloneControls,
  controlsNeedSeed,
  loadBestControlsForSystem,
  loadControlsBackup,
  resolvePadProfile,
  saveControlsBackup,
  saveLastPadProfile,
} from './controllerCache.js';
import { focusEmulatorSurface, listConnectedGamepads, pickDefaultControls } from './gamepad.js';

/** Handler ativo — EmulatorJS não chama terminate() ao sair; handlers órfãos quebram o dropdown. */
let activeGamepadHandler = null;

function gamepadOptionKey(pad) {
  return `${pad.id}_${pad.index}`;
}

function terminateGamepadHandler(handler) {
  if (!handler?.terminate) return;
  try {
    handler.terminate();
  } catch {
    /* ignore */
  }
}

function stopEmulatorGamepad(emu) {
  detachGamepadResyncListeners(emu);
  terminateGamepadHandler(emu?.gamepad);
  if (activeGamepadHandler && activeGamepadHandler === emu?.gamepad) {
    activeGamepadHandler = null;
  }
}

function refreshGamepadHandlerState(emu) {
  try {
    emu?.gamepad?.updateGamepadState?.();
  } catch {
    /* ignore */
  }
}

function normalizeGamepadSelection(emu) {
  if (!emu?.gamepadSelection?.length) return;

  refreshGamepadHandlerState(emu);

  const connectedKeys = new Set(
    listConnectedGamepads().map(({ gp }) => gamepadOptionKey(gp)),
  );

  for (let i = 0; i < emu.gamepadSelection.length; i++) {
    if (emu.gamepadSelection[i] && !connectedKeys.has(emu.gamepadSelection[i])) {
      emu.gamepadSelection[i] = '';
    }
  }

  const used = new Set(emu.gamepadSelection.filter(Boolean));
  for (const { gp } of listConnectedGamepads()) {
    const key = gamepadOptionKey(gp);
    if (used.has(key)) continue;
    const slot = emu.gamepadSelection.findIndex((value) => !value);
    if (slot >= 0) {
      emu.gamepadSelection[slot] = key;
      used.add(key);
    }
  }
}

function fixInvalidGamepadSelects(emu) {
  if (!emu?.gamepadLabels?.length) return;
  for (let i = 0; i < emu.gamepadLabels.length; i++) {
    const select = emu.gamepadLabels[i];
    if (!select?.options?.length) continue;
    const value = select.value;
    if (!value || value === 'notconnected') continue;
    const valid = Array.from(select.options).some((opt) => opt.value === value);
    if (!valid) {
      emu.gamepadSelection[i] = '';
      select.value = 'notconnected';
    }
  }
}

function syncEmulatorGamepads(emu) {
  if (!emu?.gamepadLabels?.length) return;
  normalizeGamepadSelection(emu);
  if (typeof emu.updateGamepadLabels === 'function') {
    emu.updateGamepadLabels();
  }
  fixInvalidGamepadSelects(emu);
  if (typeof emu.checkGamepadInputs === 'function') {
    emu.checkGamepadInputs();
  }
}

function detachGamepadResyncListeners(emu) {
  if (typeof emu?.__tomDetachGamepadResync === 'function') {
    emu.__tomDetachGamepadResync();
    emu.__tomDetachGamepadResync = null;
  }
}

function attachGamepadResyncListeners(emu) {
  if (!emu || emu.__tomDetachGamepadResync) return;

  const resync = () => {
    if (window.EJS_emulator !== emu) return;
    reapplyControlsIfPadChanged(emu);
    syncEmulatorGamepads(emu);
  };

  const events = ['gamepadconnected', 'gamepaddisconnected', 'focus', 'pointerdown', 'keydown'];
  events.forEach((eventName) => window.addEventListener(eventName, resync));

  emu.__tomDetachGamepadResync = () => {
    events.forEach((eventName) => window.removeEventListener(eventName, resync));
  };
}

function patchGamepadUi(emu) {
  if (!emu || emu.__tomGamepadUiPatch) return;
  emu.__tomGamepadUiPatch = true;

  const originalUpdateGamepadLabels = emu.updateGamepadLabels.bind(emu);
  emu.updateGamepadLabels = function updateGamepadLabelsWithSync() {
    normalizeGamepadSelection(emu);
    originalUpdateGamepadLabels();
    fixInvalidGamepadSelects(emu);
  };

  emu.on('start', () => {
    window.setTimeout(() => {
      reapplyControlsIfPadChanged(emu);
      syncEmulatorGamepads(emu);
      focusEmulatorSurface();
    }, 100);
  });

  attachGamepadResyncListeners(emu);

  if (emu.controlMenu) {
    const menu = emu.controlMenu;
    const observer = new MutationObserver(() => {
      if (menu.style.display === 'none') return;
      syncEmulatorGamepads(emu);
    });
    observer.observe(menu, { attributes: true, attributeFilter: ['style'] });
    emu.__tomControlMenuObserver = observer;
  }
}

function cleanupEmulatorGamepadUi(emu) {
  emu?.__tomControlMenuObserver?.disconnect?.();
  if (emu) emu.__tomControlMenuObserver = null;
}

const CDN_DATA_PATH = 'https://cdn.emulatorjs.org/stable/data/';

function useCdnDataPath() {
  if (import.meta.env.VITE_EJS_CDN === '1' || import.meta.env.VITE_EJS_CDN === 'true') {
    return true;
  }
  // Cores .wasm.data atrás de Cloudflare Bot Fight podem receber 403/HTML → runtime falha.
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host !== 'localhost' && host !== '127.0.0.1' && !/^\d+\.\d+\.\d+\.\d+$/.test(host)) {
      return true;
    }
  }
  return false;
}

/** Pasta data/ local (public/emulatorjs/data) ou CDN em produção / VITE_EJS_CDN=1. */
function getDataPath() {
  if (useCdnDataPath()) return CDN_DATA_PATH;
  return appUrl('emulatorjs/data/');
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Falha ao carregar ${src}`));
    document.head.appendChild(s);
  });
}

function loadCss(href) {
  return new Promise((resolve, reject) => {
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = href;
    l.onload = () => resolve();
    l.onerror = () => reject(new Error(`Falha ao carregar ${href}`));
    document.head.appendChild(l);
  });
}

let emulatorJsPromise = null;

/** Limpa cache de cores corrompido (ex. HTML de desafio Cloudflare guardado como .wasm.data). */
async function clearCorruptCoreCacheOnce() {
  if (!useCdnDataPath() || typeof indexedDB === 'undefined') return;
  const flag = 'ejs-core-cache-cleared-v4';
  try {
    if (sessionStorage.getItem(flag)) return;
    sessionStorage.setItem(flag, '1');
  } catch {
    /* ignore */
  }
  await new Promise((resolve) => {
    const req = indexedDB.deleteDatabase('EmulatorJS-core');
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}

function ensureEmulatorJS() {
  if (typeof window.EmulatorJS === 'function') {
    patchEmulatorJSRuntime();
    return Promise.resolve();
  }
  if (emulatorJsPromise) return emulatorJsPromise;
  emulatorJsPromise = loadCss(`${getDataPath()}emulator.min.css`)
    .then(() => loadScript(`${getDataPath()}emulator.min.js`))
    .then(() => {
      patchEmulatorJSRuntime();
    });
  return emulatorJsPromise;
}

/** Rastreia listeners window (resize) e silencia traduções em falta no CDN. */
function patchEmulatorJSRuntime() {
  const Ctor = window.EmulatorJS;
  if (!Ctor || Ctor.__tomRuntimePatch) return;

  const proto = Ctor.prototype;

  const origAdd = proto.addEventListener;
  proto.addEventListener = function (elem, events, cb) {
    const handles = origAdd.call(this, elem, events, cb);
    if (elem === window && String(events).includes('resize')) {
      this.__tomWinListeners = (this.__tomWinListeners || []).concat(handles);
    }
    return handles;
  };

  const origResize = proto.handleResize;
  proto.handleResize = function () {
    if (!this.game?.parentElement?.classList || !this.elements?.parent) return;
    try {
      return origResize.call(this);
    } catch {
      /* DOM desmontado */
    }
  };

  proto.localization = function (key, warnMissing = true) {
    if (key == null || key === '') return key;
    const text = String(key);
    if (text.includes('EmulatorJS v')) return text;
    if (!this.config.langJson) this.config.langJson = {};
    const table = this.config.langJson;
    if (!table[text]) {
      if (EMULATOR_LANG_EXTRA[text]) table[text] = EMULATOR_LANG_EXTRA[text];
      else if (warnMissing === false || !this.config.settingsLanguage) table[text] = text;
    }
    if (!table[text] && warnMissing !== false && this.config.settingsLanguage) {
      this.missingLang = this.missingLang || [];
      if (!this.missingLang.includes(text)) this.missingLang.push(text);
    }
    return table[text] || text;
  };

  const origHandleSpecialOptions = proto.handleSpecialOptions;
  if (origHandleSpecialOptions) {
    proto.handleSpecialOptions = function (...args) {
      try {
        return origHandleSpecialOptions.apply(this, args);
      } catch (err) {
        // Ignora erros de UI ausente (ex.: this.elements.menuToggle em DS/desmume).
      }
    };
  }

  Ctor.__tomRuntimePatch = true;
}

function removeEmulatorWindowListeners(emu) {
  if (!emu?.__tomWinListeners?.length) return;
  try {
    emu.removeEventListener(emu.__tomWinListeners);
  } catch {
    /* ignore */
  }
  emu.__tomWinListeners = [];
}

function resolveDefaultControllers(systemKey, padProfile) {
  const best = loadBestControlsForSystem(systemKey);
  if (best?.controls) return best.controls;
  return pickDefaultControls() || EJS_DEFAULT_CONTROLS;
}

function buildConfig(scriptPath, ejsCore, uiSystemKey, gameName, gameUrl) {
  const best = loadBestControlsForSystem(uiSystemKey);
  const padProfile = best?.padProfile || resolvePadProfile();

  const config = {
    gameUrl,
    dataPath: scriptPath,
    system: ejsCore,
    gameName,
    volume: 1,
    defaultControllers: cloneControls(resolveDefaultControllers(uiSystemKey, padProfile)),
    language: 'en-US',
    langJson: { ...EMULATOR_LANG_EXTRA },
    settingsLanguage: false,
    // Evita cores -thread (SharedArrayBuffer/COOP) inexistentes atrás do proxy.
    threads: false,
  };

  const c = ejsCore;
  config.startOnLoad =
    c === 'desmume' || c === 'desmume2015' || c === 'melonds' || c === 'nds'
      ? false
      : true;

  return { config, systemKey: uiSystemKey, padProfile };
}

function applyUserControls(emu, systemKey, padProfile) {
  const backup =
    loadControlsBackup(systemKey, padProfile) ||
    loadBestControlsForSystem(systemKey)?.controls;

  if (backup) {
    emu.controls = cloneControls(backup);
    if (typeof emu.setupKeys === 'function') emu.setupKeys();
    return true;
  }

  if (controlsNeedSeed(emu.controls)) {
    const seed =
      cloneControls(emu.defaultControllers) ||
      cloneControls(resolveDefaultControllers(systemKey, padProfile));
    emu.controls = cloneControls(seed);
    if (typeof emu.setupKeys === 'function') emu.setupKeys();
  }
  return false;
}

function reapplyControlsIfPadChanged(emu) {
  const meta = emu?.__tomControlMeta;
  if (!meta?.systemKey) return;

  const pads = listConnectedGamepads();
  let padProfile = meta.padProfile || resolvePadProfile();

  if (pads.length) {
    padProfile = resolvePadProfile();
    if (padProfile !== meta.padProfile) {
      meta.padProfile = padProfile;
      saveLastPadProfile(padProfile);
    }
  }

  const hadBackup = applyUserControls(emu, meta.systemKey, padProfile);
  if (hadBackup) {
    if (typeof emu.checkGamepadInputs === 'function') emu.checkGamepadInputs();
    syncEmulatorGamepads(emu);
  } else if (pads.length) {
    syncEmulatorGamepads(emu);
  }
}

function seedControlsIfNeeded(emu, systemKey, padProfile) {
  applyUserControls(emu, systemKey, padProfile);
}

function persistEmulatorControls(emu, systemKey, padProfile) {
  if (!emu?.controls) return;
  saveControlsBackup(systemKey, cloneControls(emu.controls), padProfile);
  saveLastPadProfile(padProfile);
}

function attachControlPersistence(emu, systemKey, padProfile) {
  if (!emu || emu.__tomControlPersistence) return;
  emu.__tomControlPersistence = true;

  const originalLoadSettings = emu.loadSettings.bind(emu);
  emu.loadSettings = function loadSettingsWithCache() {
    originalLoadSettings();
    seedControlsIfNeeded(emu, systemKey, padProfile);
    if (typeof emu.checkGamepadInputs === 'function') emu.checkGamepadInputs();
  };

  const originalSaveSettings = emu.saveSettings.bind(emu);
  emu.saveSettings = function saveSettingsWithCache() {
    originalSaveSettings();
    persistEmulatorControls(emu, systemKey, padProfile);
  };
}

function flushEmulatorSettings(emu, systemKey, padProfile) {
  if (!emu) return;
  try {
    if (!emu.settingsLoaded) emu.settingsLoaded = true;
    if (typeof emu.saveSettings === 'function') emu.saveSettings();
    else persistEmulatorControls(emu, systemKey, padProfile);
  } catch (err) {
    console.warn('Não foi possível guardar controlos:', err);
  }
}

function loadLanguage(scriptPath, config) {
  const language = 'en-US';
  const targetUrl = `${scriptPath}localization/${language}.json`;

  return fetch(targetUrl)
    .then((r) => (r.ok ? r.json() : {}))
    .catch(() => ({}))
    .then((lang) => {
      config.language = language;
      const base = lang && typeof lang === 'object' ? lang : {};
      config.langJson = { ...base, ...EMULATOR_LANG_EXTRA };
    });
}

/** Evita handleResize a aceder ao DOM depois de sair do jogo (#game desmontado). */
function detachEmulatorResize(emu) {
  if (!emu) return;
  removeEmulatorWindowListeners(emu);
  emu.handleResize = () => {};
  if (typeof emu.handleSettingsResize === 'function') {
    emu.handleSettingsResize = () => {};
  }
}

function shutdownEmulator(playerSelector) {
  const emu = window.EJS_emulator;
  const meta = emu?.__tomControlMeta;

  if (emu && meta) {
    flushEmulatorSettings(emu, meta.systemKey, meta.padProfile);
    import('./profileSync.js')
      .then(({ flushProfileControlsPush }) => flushProfileControlsPush())
      .catch(() => {});
  }

  if (!emu) return Promise.resolve();

  stopEmulatorGamepad(emu);
  cleanupEmulatorGamepadUi(emu);
  detachEmulatorResize(emu);

  try {
    emu.started = false;
    emu.callEvent('exit');
  } catch {
    /* ignore */
  }

  return new Promise((resolve) => {
    setTimeout(() => {
      stopEmulatorGamepad(emu);
      detachEmulatorResize(emu);
      const el = document.querySelector(playerSelector);
      if (el) {
        el.classList.remove('ejs_parent');
        el.innerHTML = '';
      }
      try {
        delete window.EJS_emulator;
      } catch {
        window.EJS_emulator = undefined;
      }
      resolve();
    }, 200);
  });
}

export async function startEmulator({ playerSelector, core, gameName, gameUrl }) {
  await clearCorruptCoreCacheOnce();
  await ensureEmulatorJS();

  try {
    const { pullProfileControls } = await import('./profileSync.js');
    await pullProfileControls();
  } catch {
    /* perfil remoto opcional */
  }

  await shutdownEmulator(playerSelector);

  if (gameUrl instanceof File) {
    gameName = gameUrl.name.replace(/\.[^/.]+$/, '');
  }

  const uiKey = core;
  const ejsCore = SYSTEMS[core]?.ejsCore || core;
  const dataPath = getDataPath();
  const { config, systemKey, padProfile } = buildConfig(dataPath, ejsCore, uiKey, gameName, gameUrl);
  await loadLanguage(dataPath, config);

  terminateGamepadHandler(activeGamepadHandler);
  activeGamepadHandler = null;

  const emu = new window.EmulatorJS(playerSelector, config);
  emu.config.langJson = { ...(emu.config.langJson || {}), ...EMULATOR_LANG_EXTRA };
  emu.__tomControlMeta = { systemKey, padProfile };
  attachControlPersistence(emu, systemKey, padProfile);
  patchGamepadUi(emu);
  activeGamepadHandler = emu.gamepad ?? null;
  window.EJS_emulator = emu;
}

export { shutdownEmulator };

export { openEmulatorMenu, openEmulatorControlSettings } from './emulatorMenu.js';

export const EMULATOR_PLAYER = '#game';
